import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StatsCard from '../admin-dashboard/components/StatsCard';
import AttendanceTrendsChart from './components/AttendanceTrendsChart';
import PeakHoursHeatmap from './components/PeakHoursHeatmap';
import MemberActivityBreakdown from './components/MemberActivityBreakdown';
import OccupancyMonitor from './components/OccupancyMonitor';
import AttendanceReportTable from './components/AttendanceReportTable';

const AttendanceReportDashboard = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isStaff, isTrainer, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({
    totalVisits: 0,
    averageDailyVisits: 0,
    currentOccupancy: 0,
    averageSessionDuration: 0,
    peakHourOccupancy: 0,
    memberRetentionRate: 0
  });
  const [attendanceTrends, setAttendanceTrends] = useState([]);
  const [peakHoursData, setPeakHoursData] = useState([]);
  const [memberBreakdown, setMemberBreakdown] = useState([]);
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [selectedView, setSelectedView] = useState('overview');
  const [exportLoading, setExportLoading] = useState(false);

  const isStaffMember = isGymOwner || isStaff || isTrainer;

  const dateRangeOptions = [
    { value: '7', label: '7 Days' },
    { value: '30', label: '30 Days' },
    { value: '90', label: '90 Days' },
    { value: '180', label: '6 Months' }
  ];

  const viewOptions = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'trends', label: 'Trends', icon: 'TrendingUp' },
    { id: 'peak-hours', label: 'Peak Hours', icon: 'Clock' },
    { id: 'members', label: 'Members', icon: 'Users' }
  ];

  useEffect(() => {
    if (!authLoading && (!user || !isStaffMember)) {
      navigate('/member-dashboard');
    }
  }, [user, isStaffMember, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && isStaffMember) {
      loadAttendanceData();
    }
  }, [currentTenant, isStaffMember, dateRange]);

  const loadAttendanceData = async () => {
    setLoading(true);
    try {
      const daysAgo = parseInt(dateRange);
      const startDate = startOfDay(subDays(new Date(), daysAgo));
      const endDate = endOfDay(new Date());

      // Load attendance logs
      const { data: attendanceLogs, error: logsError } = await supabase
        ?.from('attendance_logs')
        ?.select(`
          *,
          user_profiles!attendance_logs_user_id_fkey(id, full_name, email, avatar_url),
          tenant_members!inner(role, membership_plan, is_active)
        `)
        ?.eq('tenant_id', currentTenant?.id)
        ?.gte('check_in_time', startDate?.toISOString())
        ?.lte('check_in_time', endDate?.toISOString())
        ?.order('check_in_time', { ascending: false });

      if (logsError) throw logsError;

      // Calculate stats
      const totalVisits = attendanceLogs?.length || 0;
      const averageDailyVisits = Math?.round(totalVisits / daysAgo);

      // Current occupancy (checked in but not checked out today)
      const today = startOfDay(new Date());
      const currentOccupancy = attendanceLogs?.filter(log => 
        new Date(log?.check_in_time) >= today && !log?.check_out_time
      )?.length || 0;

      // Average session duration
      const completedSessions = attendanceLogs?.filter(log => log?.duration_minutes) || [];
      const averageSessionDuration = completedSessions?.length > 0
        ? Math?.round(completedSessions?.reduce((sum, log) => sum + (log?.duration_minutes || 0), 0) / completedSessions?.length)
        : 0;

      // Peak hour occupancy
      const peakHourOccupancy = calculatePeakHourOccupancy(attendanceLogs);

      // Member retention rate (members who visited in last 7 days)
      const { data: totalMembers } = await supabase
        ?.from('tenant_members')
        ?.select('id', { count: 'exact' })
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('is_active', true);

      const last7Days = startOfDay(subDays(new Date(), 7));
      const uniqueVisitors = new Set(
        attendanceLogs
          ?.filter(log => new Date(log?.check_in_time) >= last7Days)
          ?.map(log => log?.user_id)
      )?.size;

      const memberRetentionRate = totalMembers?.length > 0
        ? Math?.round((uniqueVisitors / totalMembers?.length) * 100)
        : 0;

      setStats({
        totalVisits,
        averageDailyVisits,
        currentOccupancy,
        averageSessionDuration,
        peakHourOccupancy,
        memberRetentionRate
      });

      // Process trends data
      const trendsData = processTrendsData(attendanceLogs, daysAgo);
      setAttendanceTrends(trendsData);

      // Process peak hours data
      const peakData = processPeakHoursData(attendanceLogs);
      setPeakHoursData(peakData);

      // Process member breakdown
      const breakdownData = processMemberBreakdown(attendanceLogs);
      setMemberBreakdown(breakdownData);

      // Set recent attendance
      setRecentAttendance(attendanceLogs?.slice(0, 50) || []);
    } catch (error) {
      console.error('Error loading attendance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePeakHourOccupancy = (logs) => {
    const hourCounts = {};
    logs?.forEach(log => {
      const hour = new Date(log?.check_in_time)?.getHours();
      hourCounts[hour] = (hourCounts?.[hour] || 0) + 1;
    });
    return Math?.max(...Object?.values(hourCounts), 0);
  };

  const processTrendsData = (logs, days) => {
    const trendsMap = {};
    
    // Initialize all days
    for (let i = days - 1; i >= 0; i--) {
      const date = format(subDays(new Date(), i), 'yyyy-MM-dd');
      trendsMap[date] = { date, visits: 0, uniqueMembers: new Set(), avgDuration: 0, durations: [] };
    }

    // Populate with data
    logs?.forEach(log => {
      const date = format(new Date(log?.check_in_time), 'yyyy-MM-dd');
      if (trendsMap?.[date]) {
        trendsMap[date].visits += 1;
        trendsMap?.[date]?.uniqueMembers?.add(log?.user_id);
        if (log?.duration_minutes) {
          trendsMap?.[date]?.durations?.push(log?.duration_minutes);
        }
      }
    });

    // Calculate averages
    return Object?.values(trendsMap)?.map(day => ({
      date: day?.date,
      visits: day?.visits,
      uniqueMembers: day?.uniqueMembers?.size,
      avgDuration: day?.durations?.length > 0
        ? Math?.round(day?.durations?.reduce((a, b) => a + b, 0) / day?.durations?.length)
        : 0
    }));
  };

  const processPeakHoursData = (logs) => {
    const hourData = Array?.from({ length: 24 }, (_, i) => ({
      hour: i,
      label: `${i?.toString()?.padStart(2, '0')}:00`,
      count: 0,
      weekday: 0,
      weekend: 0
    }));

    logs?.forEach(log => {
      const date = new Date(log?.check_in_time);
      const hour = date?.getHours();
      const isWeekend = date?.getDay() === 0 || date?.getDay() === 6;

      hourData[hour].count += 1;
      if (isWeekend) {
        hourData[hour].weekend += 1;
      } else {
        hourData[hour].weekday += 1;
      }
    });

    return hourData;
  };

  const processMemberBreakdown = (logs) => {
    const breakdown = {
      byMembershipType: {},
      byVisitFrequency: { regular: 0, occasional: 0, rare: 0 },
      byRole: {}
    };

    // Count by membership type
    logs?.forEach(log => {
      const membershipPlan = log?.tenant_members?.[0]?.membership_plan || 'Unknown';
      breakdown.byMembershipType[membershipPlan] = (breakdown?.byMembershipType?.[membershipPlan] || 0) + 1;

      const role = log?.tenant_members?.[0]?.role || 'member';
      breakdown.byRole[role] = (breakdown?.byRole?.[role] || 0) + 1;
    });

    // Calculate visit frequency
    const userVisits = {};
    logs?.forEach(log => {
      userVisits[log?.user_id] = (userVisits?.[log?.user_id] || 0) + 1;
    });

    Object?.values(userVisits)?.forEach(count => {
      if (count >= 15) breakdown.byVisitFrequency.regular += 1;
      else if (count >= 5) breakdown.byVisitFrequency.occasional += 1;
      else breakdown.byVisitFrequency.rare += 1;
    });

    return breakdown;
  };

  const handleExportReport = async () => {
    setExportLoading(true);
    try {
      // Create CSV content
      const csvContent = [
        ['Date', 'Member Name', 'Check In', 'Check Out', 'Duration (min)', 'Membership Type', 'Entry Type'],
        ...recentAttendance?.map(log => [
          format(new Date(log?.check_in_time), 'yyyy-MM-dd'),
          log?.user_profiles?.full_name || 'Unknown',
          format(new Date(log?.check_in_time), 'HH:mm'),
          log?.check_out_time ? format(new Date(log?.check_out_time), 'HH:mm') : 'Active',
          log?.duration_minutes || '-',
          log?.tenant_members?.[0]?.membership_plan || 'Unknown',
          log?.entry_type || 'barcode_scan'
        ])
      ]?.map(row => row?.join(','))?.join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window?.URL?.createObjectURL(blob);
      const a = document?.createElement('a');
      a.href = url;
      a.download = `attendance-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a?.click();
      window?.URL?.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
    } finally {
      setExportLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading attendance analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      
      <main className="flex-1 px-4 py-6 md:px-8 lg:px-12 max-w-[1440px] mx-auto w-full">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                Attendance Report Dashboard
              </h1>
              <p className="text-muted-foreground">
                Comprehensive analytics and insights for gym attendance patterns
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleExportReport}
                disabled={exportLoading}
                className="flex items-center gap-2"
              >
                {exportLoading ? (
                  <Icon name="Loader2" size={18} className="animate-spin" />
                ) : (
                  <Icon name="Download" size={18} />
                )}
                Export Report
              </Button>
            </div>
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-foreground">Date Range:</span>
          {dateRangeOptions?.map(option => (
            <Button
              key={option?.value}
              onClick={() => setDateRange(option?.value)}
              variant={dateRange === option?.value ? 'default' : 'outline'}
              size="sm"
            >
              {option?.label}
            </Button>
          ))}
        </div>

        {/* KPI Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <StatsCard
            icon="Users"
            iconColor="var(--color-primary)"
            title="Total Visits"
            value={stats?.totalVisits?.toLocaleString()}
            subtitle={`Last ${dateRange} days`}
            trend="neutral"
            trendValue=""
          />
          <StatsCard
            icon="Calendar"
            iconColor="var(--color-success)"
            title="Average Daily Visits"
            value={stats?.averageDailyVisits?.toLocaleString()}
            subtitle="Per day average"
            trend="up"
            trendValue="+12%"
          />
          <StatsCard
            icon="Activity"
            iconColor="var(--color-warning)"
            title="Current Occupancy"
            value={stats?.currentOccupancy?.toLocaleString()}
            subtitle="Members currently in gym"
            trend="neutral"
            trendValue=""
          />
          <StatsCard
            icon="Clock"
            iconColor="var(--color-accent)"
            title="Avg Session Duration"
            value={`${stats?.averageSessionDuration} min`}
            subtitle="Average workout time"
            trend="up"
            trendValue="+5 min"
          />
          <StatsCard
            icon="TrendingUp"
            iconColor="var(--color-primary)"
            title="Peak Hour Occupancy"
            value={stats?.peakHourOccupancy?.toLocaleString()}
            subtitle="Highest concurrent members"
            trend="neutral"
            trendValue=""
          />
          <StatsCard
            icon="UserCheck"
            iconColor="var(--color-success)"
            title="Member Retention"
            value={`${stats?.memberRetentionRate}%`}
            subtitle="Active in last 7 days"
            trend="up"
            trendValue="+3%"
          />
        </div>

        {/* View Selector */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          {viewOptions?.map(view => (
            <Button
              key={view?.id}
              onClick={() => setSelectedView(view?.id)}
              variant={selectedView === view?.id ? 'default' : 'outline'}
              className="flex items-center gap-2"
            >
              <Icon name={view?.icon} size={18} />
              {view?.label}
            </Button>
          ))}
        </div>

        {/* Dynamic Content Based on Selected View */}
        {selectedView === 'overview' && (
          <div className="space-y-6">
            <OccupancyMonitor currentOccupancy={stats?.currentOccupancy} />
            <AttendanceTrendsChart data={attendanceTrends} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PeakHoursHeatmap data={peakHoursData} />
              <MemberActivityBreakdown data={memberBreakdown} />
            </div>
          </div>
        )}

        {selectedView === 'trends' && (
          <div className="space-y-6">
            <AttendanceTrendsChart data={attendanceTrends} />
            <AttendanceReportTable data={recentAttendance} />
          </div>
        )}

        {selectedView === 'peak-hours' && (
          <div className="space-y-6">
            <PeakHoursHeatmap data={peakHoursData} />
            <AttendanceReportTable data={recentAttendance} />
          </div>
        )}

        {selectedView === 'members' && (
          <div className="space-y-6">
            <MemberActivityBreakdown data={memberBreakdown} />
            <AttendanceReportTable data={recentAttendance} />
          </div>
        )}
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default AttendanceReportDashboard;